/**
 * formatPersonName — display-side person name formatter.
 *
 * Pure display transform: never mutates stored data.
 * Apply everywhere a contact's display name is rendered.
 *
 * Rules (per issue #2432):
 * - All-lowercase or all-caps → title-case each word.
 * - Mixed-case with an uppercase letter past position 0 in any token
 *   → preserve as-is (handles "McDonald", "DeShawn", "DiCaprio").
 * - Particles / prepositions ("de", "la", "van", etc.) stay lowercase
 *   when they are not the first word in the full name.
 * - Hyphenated names: each segment is capitalised ("jean-pierre" → "Jean-Pierre").
 * - Apostrophes: "o'brien" → "O'Brien".
 * - Initials (e.g. "J.R.R."): preserved as-is.
 * - displayNameOverride: if the caller passes an override string, that
 *   value is returned verbatim (allows per-contact opt-out of auto-casing).
 */

/**
 * Name particles/prepositions that should stay lowercase unless they are
 * the leading word in the full name.  Only multi-character forms are here —
 * single-letter strings (e, s) are common initials and must NOT be lowercased.
 */
const PARTICLES = new Set([
  'de', 'del', 'della', 'den', 'des',
  'der', 'dem',
  'di', 'do', 'dos', 'du',
  'la', 'las', 'le', 'les', 'li', 'lo', 'los',
  'van', 'von', 'ver',
  'da', 'das',
  'af', 'av',
  'op',
  'ten', 'ter',
  'und', 'of', 'at', 'in', 'and',
]);

/**
 * Capitalise a single name segment, respecting hyphens and apostrophes.
 * "jean-pierre" → "Jean-Pierre"
 * "o'brien"     → "O'Brien"
 * "j.r.r."      → "J.R.R."  (initials: unchanged — each char is upper/dot)
 */
function capitaliseSegment(seg: string): string {
  if (!seg) return seg;

  // Hyphenated: capitalise each hyphen-separated sub-segment
  if (seg.includes('-')) {
    return seg.split('-').map(capitaliseSegment).join('-');
  }

  // Apostrophe: "o'brien" → "O'Brien"
  if (seg.includes("'")) {
    const [before, ...rest] = seg.split("'");
    const afterParts = rest.join("'"); // preserve any embedded apostrophes
    return capitaliseSegment(before) + "'" + capitaliseSegment(afterParts);
  }

  // Already looks like initials ("J.R.R.") — leave alone
  if (/^[A-Z]([A-Z]\.)+$/.test(seg) || /^([A-Za-z]\.)+$/.test(seg)) {
    return seg.toUpperCase().replace(/[a-z]/g, (c) => c.toUpperCase());
  }

  return seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase();
}

/**
 * Determine whether a raw name string contains intentional mixed-case —
 * meaning at least one token has an uppercase letter past position 0
 * AND that token is not all-caps.
 *
 * "McDonald"        → true  (camel-case within a token)
 * "MARK SINSHEIMER" → false (all-caps artifact — entire token is uppercase)
 * "ernee wouters"   → false (all-lowercase artifact)
 * "Mark Sinsheimer" → false (normal title-case: first char uppercase, rest lower)
 * "JEAN-PIERRE"     → false (all-caps hyphenated — split → [JEAN, PIERRE] both all-caps)
 */
function hasIntentionalMixedCase(raw: string): boolean {
  // Split on whitespace and hyphens for token analysis
  const tokens = raw.trim().split(/[\s-]+/);
  return tokens.some((tok) => {
    // Strip apostrophes for this check
    const clean = tok.replace(/'/g, '');
    if (clean.length < 2) return false;
    // Skip all-caps tokens — those are data-entry artifacts, not intentional
    if (clean === clean.toUpperCase()) return false;
    // Check for uppercase past position 0 in a non-all-caps token
    return /[A-Z]/.test(clean.slice(1));
  });
}

/**
 * Format a raw contact name for display.
 *
 * @param raw               The stored name string (may be any case).
 * @param displayNameOverride  When provided and non-empty, returned verbatim.
 */
export function formatPersonName(raw: string, displayNameOverride?: string | null): string {
  if (displayNameOverride != null) return displayNameOverride;
  if (!raw || !raw.trim()) return raw;

  const trimmed = raw.trim();

  // Preserve intentional mixed-case names (e.g. "McDonald", "DeShawn")
  if (hasIntentionalMixedCase(trimmed)) {
    return trimmed;
  }

  // Split into whitespace-delimited tokens while preserving original spacing
  // (single-space join is fine — stored names rarely have multiple spaces)
  const tokens = trimmed.split(/\s+/);

  const result = tokens.map((token, index) => {
    // Lower-only representation for particle check (strip hyphens/apostrophes)
    const lower = token.replace(/[^a-zA-Z]/g, '').toLowerCase();

    // Particles are lowercased when they are NOT the first word
    if (index > 0 && PARTICLES.has(lower)) {
      return token.toLowerCase();
    }

    return capitaliseSegment(token);
  });

  return result.join(' ');
}
